import { useCallback, useEffect, useState } from 'react';

/**
 * useAutoSave — state machine for the auto-save indicator.
 *
 * Fixes audit #8: the old "auto-saved" label was static and never reflected
 * the actual save state. This hook gives a real `idle → saving → saved Ns ago`
 * progression you can render as text.
 *
 * Usage:
 *   const save = useAutoSave();
 *
 *   // before persistence call
 *   save.markSaving();
 *   try {
 *     await persistDraft(...);
 *     save.markSaved();
 *   } catch (err) {
 *     save.markError(err.message);
 *   }
 *
 *   return <span>{save.label}</span>;
 */

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface AutoSaveState {
  status: AutoSaveStatus;
  savedAt?: number;        // Date.now() when last saved
  errorMessage?: string;
}

export interface UseAutoSaveReturn {
  state: AutoSaveState;
  /** Human-readable label suitable for direct render, or null when idle */
  label: string | null;
  markSaving: () => void;
  markSaved: () => void;
  markError: (message: string) => void;
  reset: () => void;
}

const formatAgo = (savedAt: number): string => {
  const secs = Math.floor((Date.now() - savedAt) / 1000);
  if (secs < 5) return 'Saved just now';
  if (secs < 60) return `Saved ${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `Saved ${mins}m ago`;
  const hours = Math.floor(mins / 60);
  return `Saved ${hours}h ago`;
};

export const useAutoSave = (): UseAutoSaveReturn => {
  const [state, setState] = useState<AutoSaveState>({ status: 'idle' });
  const [, forceTick] = useState(0); // re-render to update "Ns ago" label

  // While in 'saved' state, tick every 5s so the relative timestamp stays fresh
  useEffect(() => {
    if (state.status !== 'saved') return;
    const id = window.setInterval(() => forceTick(t => t + 1), 5000);
    return () => window.clearInterval(id);
  }, [state.status]);

  const markSaving = useCallback(() => setState({ status: 'saving' }), []);
  const markSaved = useCallback(() => setState({ status: 'saved', savedAt: Date.now() }), []);
  const markError = useCallback(
    (message: string) => setState({ status: 'error', errorMessage: message }),
    [],
  );
  const reset = useCallback(() => setState({ status: 'idle' }), []);

  let label: string | null = null;
  switch (state.status) {
    case 'idle':
      label = null;
      break;
    case 'saving':
      label = 'Saving…';
      break;
    case 'saved':
      label = state.savedAt ? formatAgo(state.savedAt) : 'Saved';
      break;
    case 'error':
      label = state.errorMessage ? `Save failed: ${state.errorMessage}` : 'Save failed';
      break;
  }

  return { state, label, markSaving, markSaved, markError, reset };
};
